import React, { useRef } from "react";
import PropTypes from "prop-types";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { ReactSVG } from "react-svg";
import "./Button.scss";

/**
 * Button
 * @module
 * @param {bool} [primary] - Primary button
 * @param {(bool | number)} [disabled] - Disabled button
 * @param {string} label - Label text
 * @param {string} [icon] - Icon name
 * @param {string} [iconClass] - ClassName for icon
 * @param {func} onClick - Function invoked on button click
 * @param {string} [title] - Title text
 * @param {string} [form] - Name of form
 * @param {string} [position] - Tooltip position, if not provided - top
 * @param {string} [imageUrl] - Image URL
 * @param {string} type - Type of button ("button" | "submit" | "reset")
 * @param {string} [className] - Button class name
 * @param {string} [buttonWrapperClassName] - Class name for button wrapper
 * @param {func} [customTooltipContent] - Custom tooltip content
 * @param {bool} [parentTitle] - parentTitle props for Toolbar
 */
const ButtonComponent = (props) => {
  const ref = useRef(null);
  const onClick = (e) => {
    if (!props.form && !props.type) {
      e.preventDefault();
      props.onClick(e);
    }
  };

  const getChildren = () => {
    if (props.imageUrl && props.label) {
      return <>
        <ReactSVG src={props.imageUrl} />
        {props.label}
      </>
    }
    if (props.label) return props.label;
    else if (props.imageUrl) return <ReactSVG src={props.imageUrl} />;
    else return undefined;
  }

  const className = `button-wrapper rt-button ${props.buttonWrapperClassName || ""}`;
  const button = (
    <Button
      children={getChildren()}
      className={`${props.className || ""} ${props.imageUrl || props.icon ? props.label ? "icon-text-button" : "icon-button" : ""}`}
      type={props.form ? "submit" : props.type}
      themeColor={props.primary ? "primary" : null}
      rounded={null}
      disabled={props.disabled}
      fillMode={props.fillMode || props.look ? props.fillMode || props.look : props.icon || props.imageUrl ? "flat" : "solid"}
      icon={props.imageUrl ? null : props.icon}
      onClick={onClick}
      iconClass={props.iconClass}
      form={props.form}
    />
  );

  return (
    <Tooltip
      anchorElement="target"
      content={
        props.customTooltipContent ? () => props.customTooltipContent : null
      }
      position={props.position || "top"}
      openDelay={0}
      open={props.notUseTooltip ? false : undefined}
      targetElement={ref.current}
      parentTitle={props.parentTitle ?? false}
    >
      <div
        ref={ref}
        className={className}
        title={props.title}
      >
        {button}
      </div>
    </Tooltip>
  );
};

ButtonComponent.propTypes = {
  primary: PropTypes.bool,
  notUseTooltip: PropTypes.bool,
  disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  label: PropTypes.string,
  icon: PropTypes.string,
  iconClass: PropTypes.string,
  onClick: PropTypes.func,
  title: PropTypes.string,
  form: PropTypes.string,
  imageUrl: PropTypes.string,
  type: PropTypes.string,
  className: PropTypes.string,
  buttonWrapperClassName: PropTypes.string,
  customTooltipContent: PropTypes.object,
  parentTitle: PropTypes.bool
};

export default ButtonComponent;
